import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ICSSymbolDefinition } from '@/types/annotation';

// Annotation drawing modes
export type AnnotationDrawMode = 'select' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'freehandPolyline' | 'freehandPolygon' | 'text' | 'symbol' | null;

// Measurement modes
export type MeasurementMode = 'distance' | 'area' | 'radius' | null;

// Analysis tool modes
export type AnalysisTool = 'buffer' | 'coverage' | 'route' | null;

// Measurement unit preference
export type MeasurementUnit = 'imperial' | 'metric';

// Precision mode uses ArcGIS native measurement widgets for better accuracy on imagery
export type MeasurementPrecisionMode = 'standard' | 'precision';

// Current annotation style for drawing
export interface CurrentAnnotationStyle {
  strokeColor: string;
  fillColor: string;
  fillOpacity: number;
  strokeWidth: number;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  pattern: 'solid' | 'dashed' | 'dotted';
}

// Default annotation style
export const DEFAULT_ANNOTATION_STYLE: CurrentAnnotationStyle = {
  strokeColor: '#ef4444', // red
  fillColor: '#f97316',   // orange
  fillOpacity: 0.3,
  strokeWidth: 3,
  fontSize: 16,
  fontWeight: 'normal',
  pattern: 'solid',
};

interface MapToolsState {
  // Annotation mode state
  isAnnotationModeActive: boolean;
  annotationDrawMode: AnnotationDrawMode;
  selectedAnnotationId: string | null;
  annotationToolbarExpanded: boolean;
  currentAnnotationStyle: CurrentAnnotationStyle;
  selectedSymbol: ICSSymbolDefinition | null;

  // Measurement state
  measurementMode: MeasurementMode;
  measurementUnit: MeasurementUnit;
  measurementPoints: [number, number][];
  measurementToolbarExpanded: boolean;
  measurementPrecisionMode: MeasurementPrecisionMode;

  // Analysis tool state
  activeAnalysisTool: AnalysisTool;

  // Orthophoto comparison state
  isComparisonMode: boolean;
  comparisonLeadingTaskId: string | null;
  comparisonTrailingTaskId: string | null;

  // Nearby features state
  nearbyQuery: { lat: number; lng: number; radius: number } | null;
  isNearbyPanelOpen: boolean;

  // Actions
  toggleAnnotationMode: () => void;
  setAnnotationDrawMode: (mode: AnnotationDrawMode) => void;
  selectAnnotation: (id: string | null) => void;
  setAnnotationStyle: (style: Partial<CurrentAnnotationStyle>) => void;
  toggleAnnotationToolbar: () => void;
  setSelectedSymbol: (symbol: ICSSymbolDefinition | null) => void;

  setMeasurementMode: (mode: MeasurementMode) => void;
  setMeasurementUnit: (unit: MeasurementUnit) => void;
  addMeasurementPoint: (point: [number, number]) => void;
  clearMeasurementPoints: () => void;
  toggleMeasurementToolbar: () => void;
  setMeasurementPrecisionMode: (mode: MeasurementPrecisionMode) => void;

  setActiveAnalysisTool: (tool: AnalysisTool) => void;

  setComparisonMode: (enabled: boolean) => void;
  setComparisonLeadingTaskId: (taskId: string | null) => void;
  setComparisonTrailingTaskId: (taskId: string | null) => void;

  setNearbyQuery: (query: { lat: number; lng: number; radius: number } | null) => void;
  clearNearbyQuery: () => void;
  toggleNearbyPanel: () => void;

  resetToolsState: () => void;
}

const INITIAL_TOOLS_STATE = {
  isAnnotationModeActive: false,
  annotationDrawMode: null as AnnotationDrawMode,
  selectedAnnotationId: null as string | null,
  annotationToolbarExpanded: true,
  currentAnnotationStyle: { ...DEFAULT_ANNOTATION_STYLE },
  selectedSymbol: null as ICSSymbolDefinition | null,
  measurementMode: null as MeasurementMode,
  measurementUnit: 'imperial' as MeasurementUnit,
  measurementPoints: [] as [number, number][],
  measurementToolbarExpanded: true,
  measurementPrecisionMode: 'standard' as MeasurementPrecisionMode,
  activeAnalysisTool: null as AnalysisTool,
  isComparisonMode: false,
  comparisonLeadingTaskId: null as string | null,
  comparisonTrailingTaskId: null as string | null,
  nearbyQuery: null as { lat: number; lng: number; radius: number } | null,
  isNearbyPanelOpen: false,
};

export const useMapToolsStore = create<MapToolsState>()(
  persist(
    (set) => ({
      ...INITIAL_TOOLS_STATE,

      // Toggle annotation mode
      toggleAnnotationMode: () =>
        set((state) => ({
          isAnnotationModeActive: !state.isAnnotationModeActive,
          annotationDrawMode: state.isAnnotationModeActive ? null : 'select',
          selectedAnnotationId: null,
          // Clear measurement mode when activating annotation mode
          measurementMode: null,
          measurementPoints: [],
        })),

      // Set annotation draw mode
      setAnnotationDrawMode: (mode) =>
        set({
          annotationDrawMode: mode,
          selectedAnnotationId: null,
          // Only clear measurement when switching to an active draw mode, not 'select'
          ...(mode && mode !== 'select'
            ? { measurementMode: null, measurementPoints: [] }
            : {}),
        }),

      // Select an annotation
      selectAnnotation: (id) =>
        set({
          selectedAnnotationId: id,
          annotationDrawMode: id ? 'select' : null,
        }),

      // Update annotation style
      setAnnotationStyle: (style) =>
        set((state) => ({
          currentAnnotationStyle: { ...state.currentAnnotationStyle, ...style },
        })),

      // Toggle annotation toolbar expanded/collapsed
      toggleAnnotationToolbar: () =>
        set((state) => ({
          annotationToolbarExpanded: !state.annotationToolbarExpanded,
        })),

      // Set selected ICS symbol. Only force the draw mode when a symbol is
      // selected — clearing the symbol must NOT touch the draw mode, because
      // the toolbar clears the symbol right after activating any other tool
      // (text/line/polygon/...), which would clobber the just-set mode.
      setSelectedSymbol: (symbol) =>
        set(
          symbol
            ? { selectedSymbol: symbol, annotationDrawMode: 'symbol' }
            : { selectedSymbol: null }
        ),

      // Set measurement mode
      setMeasurementMode: (mode) =>
        set({
          measurementMode: mode,
          measurementPoints: [],
          // Clear annotation mode when measurement is activated
          ...(mode
            ? {
                isAnnotationModeActive: false,
                annotationDrawMode: null,
                selectedSymbol: null,
              }
            : {}),
        }),

      // Set measurement unit
      setMeasurementUnit: (unit) => set({ measurementUnit: unit }),

      // Add a measurement point
      addMeasurementPoint: (point) =>
        set((state) => ({
          measurementPoints: [...state.measurementPoints, point],
        })),

      // Clear measurement points
      clearMeasurementPoints: () =>
        set({
          measurementPoints: [],
        }),

      // Toggle measurement toolbar expanded/collapsed
      toggleMeasurementToolbar: () =>
        set((state) => ({
          measurementToolbarExpanded: !state.measurementToolbarExpanded,
        })),

      // Set measurement precision mode (standard vs ArcGIS native widgets)
      setMeasurementPrecisionMode: (mode) =>
        set({
          measurementPrecisionMode: mode,
          // Clear points when switching modes since the widget takes over
          measurementPoints: [],
        }),

      // Set active analysis tool (clears other interactive modes)
      setActiveAnalysisTool: (tool) =>
        set((state) => ({
          activeAnalysisTool: tool,
          // Clear measurement and annotation modes when activating analysis
          ...(tool ? {
            measurementMode: null,
            measurementPoints: [],
            isAnnotationModeActive: false,
            annotationDrawMode: null,
            selectedSymbol: null,
          } : {}),
        })),

      // Set comparison mode
      setComparisonMode: (enabled) =>
        set({
          isComparisonMode: enabled,
          // Clear other modes when entering comparison
          ...(enabled
            ? {
                measurementMode: null,
                measurementPoints: [],
                isAnnotationModeActive: false,
                annotationDrawMode: null,
                selectedSymbol: null,
              }
            : {
                comparisonLeadingTaskId: null,
                comparisonTrailingTaskId: null,
              }),
        }),

      // Set comparison leading task
      setComparisonLeadingTaskId: (taskId) =>
        set({ comparisonLeadingTaskId: taskId }),

      // Set comparison trailing task
      setComparisonTrailingTaskId: (taskId) =>
        set({ comparisonTrailingTaskId: taskId }),

      // Set nearby query
      setNearbyQuery: (query) =>
        set({ nearbyQuery: query, isNearbyPanelOpen: !!query }),

      // Clear nearby query
      clearNearbyQuery: () =>
        set({ nearbyQuery: null, isNearbyPanelOpen: false }),

      // Toggle nearby panel
      toggleNearbyPanel: () =>
        set((state) => ({
          isNearbyPanelOpen: !state.isNearbyPanelOpen,
          nearbyQuery: state.isNearbyPanelOpen ? null : state.nearbyQuery,
        })),

      // Reset tools state to defaults
      resetToolsState: () => set({ ...INITIAL_TOOLS_STATE }),
    }),
    {
      name: 'map-tools-storage',
      partialize: (state) => ({
        currentAnnotationStyle: state.currentAnnotationStyle,
        measurementUnit: state.measurementUnit,
        measurementPrecisionMode: state.measurementPrecisionMode,
      }),
    }
  )
);
